import { useForm } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { z } from "zod";
import logo from "assets/img/compass-logo-login.webp";
import { Button, Spinner, ValidationMessage } from "components/elements";
import { requestResetLink } from "../lib/requestResetLink";

const schema = z.object({
    email: z.string().email("Please enter a valid email address"),
});

type FormData = z.infer<typeof schema>;

export const ForgotPassword = () => {
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            email: "",
        },
        validators: {
            onSubmit: schema,
        },
        onSubmit: async ({ value }) => {
            const { email } = value;
            try {
                await requestResetLink(email);

                enqueueSnackbar(
                    "If an account exists for that email, we've sent a password reset link.",
                    { variant: "success" },
                );
                setStatus("success");
            } catch (err: any) {
                setError(err.message);
                setStatus("error");
            }
        },
    });

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f7fbfc] px-4">
            <div className="w-full max-w-sm">
                <div className="mb-8 text-center">
                    <img src={logo} alt="Compass Logo" className="mx-auto h-20" />
                </div>

                <h1 className="mb-4 text-center text-xl font-bold">Forgot Password</h1>

                {status === "success" ? (
                    <div className="space-y-3 text-center text-green-600">
                        <p>
                            If an account exists for that email, we've sent a password reset link.
                        </p>
                        <Button
                            type="button"
                            onClick={() => navigate({ to: "/login" })}
                            className="w-full"
                        >
                            Back to login
                        </Button>
                    </div>
                ) : (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                        className="space-y-4"
                        method="POST"
                    >
                        <div>
                            <label className="mb-1 block text-sm text-gray-600">
                                Email Address
                            </label>
                            <form.Field name="email">
                                {(field) => (
                                    <>
                                        <input
                                            name={field.name}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            type="email"
                                            className="w-full rounded border border-[#6f7070] px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                                        />
                                        <ValidationMessage
                                            error={field.state.meta.errors[0]?.message}
                                        />
                                    </>
                                )}
                            </form.Field>
                        </div>

                        {error && <p className="text-sm text-red-500">{error}</p>}

                        <form.Subscribe selector={(state) => [state.isSubmitting]}>
                            {([isSubmitting]) => (
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-3 text-white"
                                >
                                    {isSubmitting ? (
                                        <Spinner className="h-4 w-4" />
                                    ) : (
                                        "Send Reset Link"
                                    )}
                                </Button>
                            )}
                        </form.Subscribe>
                        <Link
                            to="/login"
                            className="mt-2 block w-full text-center text-sm text-gray-600 hover:underline"
                        >
                            ← Back to Login
                        </Link>
                    </form>
                )}
            </div>
        </div>
    );
};
